import { cloneDeep } from '@apollo/client/utilities';
import { Close, Save } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from '@mui/material';
import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import type {
    EOpportunityRequestTypes,
    IOpportunityNew,
    TOpportunityRequestingState,
} from '@sharedInterfaces/IOpportunity';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { IAnalyseOpportunity } from '@sharedInterfaces/websocket/IAnalyseOpportunity';
import createOpportunity from '@src/APIs/graphQl/Opportunity/createOpportunity';
import editOpportunity from '@src/APIs/graphQl/Opportunity/editOpportunity';
import getSuggestedEmployees from '@src/APIs/graphQl/Opportunity/getSuggestedEmployees';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import { EmployeeTab } from '@src/App/Components/Core/Forms/OpportunityForm/EmployeeTab';
import { combinedKnowledge, urlRegex } from '@src/helper/helper';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import Opportunity from '@src/Objects/Opportunity';
import { updateOpportunity } from '@store/reducer/opportunityRecuder';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MvpOpportunityAiStep from './MvpOpportunityAiStep';
import MvpOpportunityEditForm from './MvpOpportunityEditForm';

interface MvpOpportunityDialogProps {
    open: boolean;
    onClose: () => void;
    opportunity?: Opportunity;
    onSave?: (opportunity: Opportunity) => void;
    initialTab?: 'details' | 'suggestions';
    resolve?: (data?: unknown) => void;
}

/**
 * Modern MVP Opportunity Dialog with fresh design and theme awareness
 */
function MvpOpportunityDialog({
    open,
    onClose,
    opportunity: originalOpportunity,
    onSave,
    initialTab = 'details',
    resolve,
}: MvpOpportunityDialogProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;
    const permissions = useSelector((state: AppState) => state.permissions);
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);

    const [createType, setCreateType] = useState<null | 'manuel' | 'ai'>(originalOpportunity ? 'manuel' : null);
    const [activeTab, setActiveTab] = useState(initialTab === 'suggestions' ? 1 : 0);
    const [saving, setSaving] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [aiConfirmation, setAiConfirmation] = useState(false);
    const [text, setText] = useState('');
    const [subKey, setSubKey] = useState('');
    const [loadingText, setLoadingText] = useState('');
    const [dialogStep, setDialogStep] = useState(0);

    const [requestingState, setRequestingState] = useState<TOpportunityRequestingState>({
        title: false,
        externalId: false,
        projectDates: false,
        projectNumbers: false,
        customerPartner: false,
        source: false,
        location: false,
        roles: false,
        competences: false,
        skills: false,
        certificates: false,
    });

    const [stateOpportunity, setStateOpportunity] = useState<IOpportunityNew>(
        originalOpportunity
            ? (cloneDeep(originalOpportunity) as IOpportunityNew)
            : ({
                  title: '',
                  externalId: '',
                  projectStart: undefined,
                  projectEnd: undefined,
                  publishDate: undefined,
                  deadline: undefined,
                  dayPrice: 0,
                  loc: '',
                  customer: '',
                  partner: '',
                  sourceName: '',
                  sourceURL: '',
                  utilization: 0,
                  remote: 0,
                  headCount: 0,
                  requestText: '',
                  skills: [],
                  selectedEmployees: [],
                  competences: [],
                  certificates: [],
                  roles: [],
                  primaryRole: null,
                  status: '',
                  shortDescription: '',
                  category: '',
              } as any),
    );

    const { combinedSkills, combinedCertificates, combinedRoles } = combinedKnowledge(
        stateOpportunity as Opportunity,
        allCompetences,
    );

    const validateForm = (): boolean => {
        const isSourceURLValid = stateOpportunity.sourceURL === '' || urlRegex.test(stateOpportunity.sourceURL);
        const isTitleValid = stateOpportunity.title !== '';
        return isSourceURLValid && isTitleValid;
    };

    const handleSave = async () => {
        setSaving(true);
        setErrorText('');

        if (!validateForm()) {
            setErrorText(langStrings.newOpportunityError);
            setSaving(false);
            return;
        }

        try {
            let result;
            if (originalOpportunity) {
                // Filter out frontend-only fields before sending to backend
                const { shortDescription, category, ...opportunityData } = stateOpportunity as any;
                result = await editOpportunity({
                    ...opportunityData,
                    id: originalOpportunity.id,
                });
            } else {
                // Filter out frontend-only fields before sending to backend
                const { shortDescription, category, ...opportunityData } = stateOpportunity as any;
                result = await createOpportunity(opportunityData);
            }

            const entity = new Opportunity(result);
            dispatch(updateOpportunity(entity));
            onSave?.(entity);
            resolve ? resolve(entity) : onClose();

            // Navigate to opportunities list after creation
            if (!originalOpportunity) {
                navigate('/sales/opportunities/list');
            }
        } catch (error) {
            setErrorText(error?.toString() || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const topic = EWebsocketTopics.analyseOpportunity;
    const readRoles = checkPermissions('Roles', 'Retrieve', permissions);

    const requestAnalyseByType = (type: EOpportunityRequestTypes | EOpportunityRequestTypes[]) => {
        const request = {
            title: false,
            externalId: false,
            projectDates: false,
            projectNumbers: false,
            customerPartner: false,
            source: false,
            location: false,
            roles: false,
            competences: false,
            skills: false,
            certificates: false,
        };
        if (Array.isArray(type)) {
            type.forEach((t) => {
                request[t] = true;
            });
        } else {
            request[type] = true;
        }
        WebSocketClient.sendMessage(topic, {
            text: stateOpportunity.requestText,
            key: subKey,
            entityId: originalOpportunity?.id || undefined,
            request,
        });
        if (Array.isArray(type)) {
            setRequestingState((prevState) => ({
                ...prevState,
                ...type.reduce((acc, t) => ({ ...acc, [t]: true }), {}),
            }));
        } else {
            setRequestingState((prevState) => ({ ...prevState, [type]: true }));
        }
    };

    const startAnalyse = () => {
        setLoadingText(langStrings.waitForAi || 'Warten auf KI-Analyse...');
        setDialogStep(1);
        setStateOpportunity((prevState) => ({ ...prevState, requestText: text }));

        const request = {
            title: true,
            externalId: false,
            projectDates: true,
            projectNumbers: true,
            customerPartner: true,
            source: false,
            location: true,
            roles: readRoles,
            competences: true,
            skills: true,
            certificates: true,
        };
        WebSocketClient.sendMessage(topic, {
            text,
            key: subKey,
            request,
        });
        setRequestingState(request);
    };

    const requestAnalyse = (field: string) => {
        const fieldMap: Record<string, EOpportunityRequestTypes> = {
            title: 'title' as EOpportunityRequestTypes,
            customer: 'customerPartner' as EOpportunityRequestTypes,
            projectStart: 'projectDates' as EOpportunityRequestTypes,
            dayPrice: 'projectNumbers' as EOpportunityRequestTypes,
            headCount: 'projectNumbers' as EOpportunityRequestTypes,
            location: 'location' as EOpportunityRequestTypes,
        };
        const mappedType = fieldMap[field];
        if (mappedType) {
            requestAnalyseByType(mappedType);
        }
    };

    React.useEffect(() => {
        const subscriptionKey = WebSocketClient.subscribe<IAnalyseOpportunity>(topic, (data: IAnalyseOpportunity) => {
            if (subscriptionKey !== data.key) return;
            if (data.error) {
                setErrorText(data.error);
                return;
            }
            setLoadingText('');
            if (createType === 'ai') {
                setCreateType('manuel');
            }

            const updateOppo: {
                title?: string;
                externalId?: string;
                projectStart?: Date;
                projectEnd?: Date;
                publishDate?: Date;
                deadline?: Date;
                dayPrice?: number;
                utilization?: number;
                remote?: number;
                headCount?: number;
                loc?: string;
                customer?: string;
                partner?: string;
                sourceName?: string;
                sourceURL?: string;
                roles?: any[];
                primaryRole?: any;
                competences?: any[];
                skills?: any[];
                certificates?: any[];
            } = {};

            if (data.type === 'title') {
                setRequestingState((prevState) => ({ ...prevState, title: false }));
            } else if (data.type === 'externalId') {
                setRequestingState((prevState) => ({ ...prevState, externalId: false }));
            } else if (data.type === 'projectDates') {
                setRequestingState((prevState) => ({ ...prevState, projectDates: false }));
            } else if (data.type === 'projectNumbers') {
                setRequestingState((prevState) => ({ ...prevState, projectNumbers: false }));
            } else if (data.type === 'customerPartner') {
                setRequestingState((prevState) => ({ ...prevState, customerPartner: false }));
            } else if (data.type === 'source') {
                setRequestingState((prevState) => ({ ...prevState, source: false }));
            } else if (data.type === 'location') {
                setRequestingState((prevState) => ({ ...prevState, location: false }));
            } else if (data.type === 'roles') {
                setRequestingState((prevState) => ({ ...prevState, roles: false }));
            } else if (data.type === 'competences') {
                setRequestingState((prevState) => ({ ...prevState, competences: false }));
            } else if (data.type === 'skills') {
                setRequestingState((prevState) => ({ ...prevState, skills: false }));
            } else if (data.type === 'certificates') {
                setRequestingState((prevState) => ({ ...prevState, certificates: false }));
            }

            if (data.title !== undefined) {
                updateOppo['title'] = data.title;
            }
            if (data.externalId !== undefined) {
                updateOppo['externalId'] = data.externalId;
            }
            if (data.projectStart !== undefined && data.projectStart !== null) {
                updateOppo['projectStart'] = new Date(data.projectStart);
            }
            if (data.projectEnd !== undefined && data.projectEnd !== null) {
                updateOppo['projectEnd'] = new Date(data.projectEnd);
            }
            if (data.publishDate !== undefined && data.publishDate !== null) {
                updateOppo['publishDate'] = new Date(data.publishDate);
            }
            if (data.deadline !== undefined && data.deadline !== null) {
                updateOppo['deadline'] = new Date(data.deadline);
            }
            if (data.dayPrice !== undefined && data.dayPrice !== null) {
                updateOppo['dayPrice'] = data.dayPrice;
            }
            if (data.utilization !== undefined && data.utilization !== null) {
                updateOppo['utilization'] = data.utilization;
            }
            if (data.remote !== undefined && data.remote !== null) {
                updateOppo['remote'] = data.remote;
            }
            if (data.headCount !== undefined && data.headCount !== null) {
                updateOppo['headCount'] = data.headCount;
            }
            if (data.location !== undefined && data.location !== null) {
                updateOppo['loc'] = data.location;
            }
            if (data.customer !== undefined && data.customer !== null) {
                updateOppo['customer'] = data.customer;
            }
            if (data.partner !== undefined && data.partner !== null) {
                updateOppo['partner'] = data.partner;
            }
            if (data.sourceName !== undefined && data.sourceName !== null) {
                updateOppo['sourceName'] = data.sourceName;
            }
            if (data.sourceURL !== undefined && data.sourceURL !== null) {
                updateOppo['sourceURL'] = data.sourceURL;
            }
            if (data.roles !== undefined && data.roles !== null) {
                updateOppo['roles'] = data.roles;
            }
            if (data.primaryRole !== undefined && data.primaryRole !== null) {
                updateOppo['primaryRole'] = data.primaryRole;
            }
            if (data.competences !== undefined && data.competences !== null) {
                updateOppo['competences'] = data.competences;
            }
            if (data.skills !== undefined && data.skills !== null) {
                updateOppo['skills'] = data.skills;
            }
            if (data.certificates !== undefined && data.certificates !== null) {
                updateOppo['certificates'] = data.certificates;
            }
            setStateOpportunity((prevState) => ({ ...prevState, ...updateOppo }));
        });
        setSubKey(subscriptionKey);
        return () => {
            return WebSocketClient.unsubscribe(subscriptionKey);
        };
    }, []);

    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return (
                    <MvpOpportunityEditForm
                        opportunity={stateOpportunity}
                        requestingState={requestingState}
                        requestAnalyse={requestAnalyseByType}
                        setOpportunity={setStateOpportunity}
                    />
                );
            case 1:
                return (
                    <Box sx={{ p: 2 }}>
                        <EmployeeTab
                            neededSkills={combinedSkills.map((s) => ({ id: s.id, title: s.title, level: s.level }))}
                            neededCertificates={combinedCertificates.map((s) => ({ id: s.id, title: s.title }))}
                            neededRoles={combinedRoles.map((s) => ({ id: s.id, title: s.title }))}
                            headCount={stateOpportunity.headCount}
                            selectedEmployees={stateOpportunity.selectedEmployees}
                            setSelectedEmployees={(val) =>
                                setStateOpportunity((prevOppo) => ({
                                    ...prevOppo,
                                    selectedEmployees: val,
                                }))
                            }
                        />
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    minHeight: '70vh',
                    background: theme.palette.background.paper,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {originalOpportunity ? originalOpportunity.title : langStrings.newOpportunity}
                </Typography>
                <IconButton
                    onClick={() => {
                        setCreateType(originalOpportunity ? 'manuel' : null);
                        setActiveTab(0);
                        setErrorText('');
                        resolve ? resolve() : onClose();
                    }}
                    sx={{ color: 'text.secondary' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            {originalOpportunity && createType === 'manuel' && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} centered>
                        <Tab label="Anfrage Details" />
                        <Tab label="Vorschläge" />
                    </Tabs>
                </Box>
            )}

            <DialogContent sx={{ p: 0, minHeight: 400 }}>
                {createType === null ? (
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', gap: 3 }}>
                        <Box
                            onClick={() => setCreateType('manuel')}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                gap: 2,
                                p: 4,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? `${theme.palette.primary.main}08`
                                        : `${theme.palette.primary.main}05`,
                                color: 'text.primary',
                                minWidth: 250,
                                minHeight: 150,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? `${theme.palette.primary.main}12`
                                            : `${theme.palette.primary.main}08`,
                                    borderColor: theme.palette.primary.main,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
                                },
                            }}
                        >
                            {getAppIcon(EIconCategory.EDIT_OPPORTUNITY)}
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, textAlign: 'center', color: 'primary.main' }}
                            >
                                Anfrage manuell ausfüllen
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                {langStrings.emptyRequestDescription || 'Anfrage manuell ausfüllen'}
                            </Typography>
                        </Box>

                        <Box
                            onClick={() => setCreateType('ai')}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                gap: 2,
                                p: 4,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? `${theme.palette.secondary.main}08`
                                        : `${theme.palette.secondary.main}05`,
                                color: 'text.primary',
                                minWidth: 250,
                                minHeight: 150,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? `${theme.palette.secondary.main}12`
                                            : `${theme.palette.secondary.main}08`,
                                    borderColor: theme.palette.secondary.main,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${theme.palette.secondary.main}20`,
                                },
                            }}
                        >
                            {getAppIcon(EIconCategory.CV_ANALYSE)}
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, textAlign: 'center', color: 'secondary.main' }}
                            >
                                Anfrage mit T4lo Assistent ausfüllen
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                Du kannst einen Anfragetext einfügen und mit T4lo auswerten lassen.
                            </Typography>
                        </Box>
                    </Box>
                ) : createType === 'ai' ? (
                    <Box>
                        {loadingText ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {loadingText}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    {/* Add loading animation here if needed */}
                                </Box>
                            </Box>
                        ) : dialogStep === 0 ? (
                            <MvpOpportunityAiStep
                                onAnalyze={startAnalyse}
                                loading={false}
                                error={errorText}
                                aiConfirmation={aiConfirmation}
                                setAiConfirmation={setAiConfirmation}
                                setText={setText}
                            />
                        ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Analyse abgeschlossen
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Die KI-Analyse wurde erfolgreich durchgeführt. Sie können nun die Daten überprüfen
                                    und bearbeiten.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        setCreateType('manuel');
                                        setDialogStep(0);
                                    }}
                                >
                                    Zu den Details
                                </Button>
                            </Box>
                        )}
                    </Box>
                ) : originalOpportunity ? (
                    renderTabContent()
                ) : (
                    <Box sx={{ p: 2 }}>
                        <MvpOpportunityEditForm
                            opportunity={stateOpportunity}
                            requestingState={requestingState}
                            requestAnalyse={requestAnalyseByType}
                            setOpportunity={setStateOpportunity}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'center',
                }}
            >
                {createType === 'ai' && dialogStep === 0 ? (
                    <Button
                        onClick={startAnalyse}
                        startIcon={<Save />}
                        variant="contained"
                        size="large"
                        disabled={!aiConfirmation || !text.trim()}
                        sx={{
                            px: 6,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                        }}
                    >
                        {langStrings.analyse || 'Analysieren'}
                    </Button>
                ) : createType === 'ai' && dialogStep === 1 && loadingText === '' ? (
                    <>
                        <Button
                            onClick={() => {
                                setDialogStep(0);
                                setLoadingText('');
                                setErrorText('');
                            }}
                            variant="outlined"
                        >
                            {langStrings.back || 'Zurück'}
                        </Button>
                        <Button
                            onClick={() => {
                                setCreateType('manuel');
                                setDialogStep(0);
                            }}
                            variant="contained"
                        >
                            {langStrings.okay || 'Weiter'}
                        </Button>
                    </>
                ) : createType === 'manuel' ? (
                    <Button
                        onClick={handleSave}
                        startIcon={<Save />}
                        variant="contained"
                        size="large"
                        disabled={saving || !validateForm()}
                        sx={{
                            px: 6,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                        }}
                    >
                        {saving ? 'Speichern...' : originalOpportunity ? langStrings.save : langStrings.create}
                    </Button>
                ) : null}
            </DialogActions>

            {errorText && (
                <Box
                    sx={{
                        p: 2,
                        bgcolor: theme.palette.error.light,
                        borderTop: `1px solid ${theme.palette.error.main}`,
                        color: 'error.main',
                    }}
                >
                    <Typography variant="body2">{errorText}</Typography>
                </Box>
            )}
        </Dialog>
    );
}

export default MvpOpportunityDialog;
